/**
 * The "when" line for OLM popups (contract §8 honesty rules). Kept out
 * of explore.tsx so the copy is unit-testable (vitest) without Leaflet
 * — same split as dc311-popup.ts.
 *
 * Two cases, keyed on properties.date_basis:
 *
 *   date_basis === 'photo_dates' — the backend's enrich-olm-dates
 *   backfill matched real per-photo date_taken values to the cluster
 *   and stamped observed_first (earliest photo) + observed_at (latest
 *   photo, overlaid from the table column). The line becomes
 *   "volunteer photos {Mon YYYY} – {Mon YYYY}" (single "{Mon YYYY}"
 *   when first and last fall in the same month), plus
 *   " · may be outdated" when the latest photo is older than 18 months
 *   — a 2021 snapshot shouldn't read like current conditions.
 *
 *   Anything else — the pre-enrichment snapshot semantics: OLM rows
 *   carry a real ingested_at but a fabricated observed_at, so we show
 *   the import date as a plain calendar date, never a relative "N ago"
 *   derived from the fake timestamp. (dc311 rows are the opposite:
 *   real observed_at → true relative date, see dc311-popup.ts.)
 */

// Fixed English month labels rendered from the UTC month: the range is
// a property of the dataset, so it must not shift with the viewer's
// timezone (an eastern-US viewer must not see "Oct 2023" for a photo
// taken 2023-11-01T00:30Z).
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** The latest photo is "outdated" once it is more than this many months old. */
export const OLM_OUTDATED_AFTER_MONTHS = 18;

function parseMs(v: unknown): number | null {
  if (typeof v !== 'string' || !v) return null;
  const t = new Date(v).getTime();
  return Number.isFinite(t) ? t : null;
}

/** "Mon YYYY" (UTC month) for a valid epoch-ms timestamp. */
export function formatMonthYear(ms: number): string {
  const d = new Date(ms);
  return `${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

/**
 * "photos imported {date}" date part for the snapshot fallback.
 * Locale-formatted (it's the viewer's calendar-date reading of the
 * import moment, not a dataset property like the month range above).
 */
export function formatIngestedDate(ingestedAt: unknown): string {
  if (typeof ingestedAt === 'string' && ingestedAt) {
    const d = new Date(ingestedAt);
    if (Number.isFinite(d.getTime())) {
      return d.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    }
  }
  return 'date unknown';
}

export interface OlmWhenProps {
  date_basis?: unknown;
  /** Earliest matched photo (enrichment) — feature.properties.observed_first. */
  observed_first?: unknown;
  /** Latest matched photo (enrichment) or the fabricated snapshot stamp. */
  observed_at?: unknown;
  ingested_at?: unknown;
}

/**
 * Build the OLM popup's "when" line. `nowMs` defaults to Date.now() at
 * CALL time (popup render), so the outdated check is safe against
 * build-time snapshotting; tests pin it.
 */
export function olmWhenLine(p: OlmWhenProps, nowMs: number = Date.now()): string {
  if (p.date_basis === 'photo_dates') {
    const first = parseMs(p.observed_first);
    const last = parseMs(p.observed_at);
    if (first !== null && last !== null) {
      const firstLabel = formatMonthYear(first);
      const lastLabel = formatMonthYear(last);
      const range = firstLabel === lastLabel ? firstLabel : `${firstLabel} – ${lastLabel}`;
      // Calendar-month arithmetic (18 months back from now), matching
      // how a reader counts "18 months ago" — not a day-count approximation.
      const cutoff = new Date(nowMs);
      cutoff.setUTCMonth(cutoff.getUTCMonth() - OLM_OUTDATED_AFTER_MONTHS);
      const outdated = last < cutoff.getTime();
      return `volunteer photos ${range}${outdated ? ' · may be outdated' : ''}`;
    }
    // Enrichment stamp without usable dates → fall through to the
    // honest snapshot line rather than rendering "volunteer photos
    // Invalid Date".
  }
  return `photos imported ${formatIngestedDate(p.ingested_at)}`;
}
